import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { MessageCircle, ChevronRight } from "lucide-react";

function formatTime(iso) {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString();
}

export default function Messages() {
  const { user } = useAuth();
  const { subscribe } = useWS();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/messages/conversations");
      setConversations(r.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
  }, [user]);

  // Live updates: refresh inbox when any message-related WS event arrives
  useEffect(() => {
    if (!user) return;
    return subscribe((evt) => {
      if (evt.type === "message:new" || evt.type === "conversation:read") {
        load();
      }
    });
  }, [subscribe, user]);

  if (!user) {
    return (
      <div className="md:ml-60 min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF007F] mb-3">
            // auth.required
          </p>
          <h2 className="font-display font-black text-3xl mb-4">
            sign in to message
          </h2>
          <button
            data-testid="messages-go-login-btn"
            onClick={() => navigate("/login")}
            className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-6 py-3"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-3xl mx-auto pt-10 px-6">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00FFFF] mb-2">
          // direct.transmission
        </p>
        <h1 className="font-display font-black text-4xl mb-2 flex items-center gap-3">
          Inbox
          <MessageCircle className="h-7 w-7 text-[#FF007F]" />
        </h1>
        <p className="text-white/50 text-sm mb-10">
          Private channels with other accounts.
        </p>

        {loading ? (
          <div className="font-mono text-xs uppercase tracking-widest text-white/40">
            loading conversations…
          </div>
        ) : conversations.length === 0 ? (
          <div
            data-testid="messages-empty"
            className="border border-white/10 p-10 text-center"
          >
            <p className="font-display text-xl mb-2">No conversations yet</p>
            <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-6">
              start one from a creator's profile
            </p>
            <Link
              to="/search"
              data-testid="messages-find-user-btn"
              className="inline-block border border-[#00FFFF]/40 hover:border-[#00FFFF] hover:bg-[#00FFFF]/10 text-[#00FFFF] font-mono text-xs uppercase tracking-widest px-5 py-3 transition-all"
            >
              find someone to message →
            </Link>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="conversation-list">
            {conversations.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/messages/${c.id}`}
                  data-testid={`conversation-${c.id}`}
                  className="flex items-center gap-4 p-4 border border-white/10 hover:border-[#00FFFF]/50 hover:bg-[#00FFFF]/5 transition-all group"
                >
                  {c.peer?.picture ? (
                    <img
                      src={picUrl(c.peer.picture)}
                      alt=""
                      className="h-12 w-12 object-cover border border-white/10 flex-shrink-0"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-[#FF007F]/20 flex items-center justify-center font-display font-bold text-[#FF007F] flex-shrink-0">
                      {c.peer?.name?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-semibold text-white truncate">
                        {c.peer?.name || "(unknown)"}
                      </p>
                      <p className="font-mono text-[10px] text-white/40 flex-shrink-0">
                        {formatTime(c.last_message_at)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <p
                        className={`text-xs truncate ${
                          c.unread_count > 0
                            ? "text-white font-semibold"
                            : "text-white/50"
                        }`}
                      >
                        {c.last_message || "Say hi 👋"}
                      </p>
                      {c.unread_count > 0 && (
                        <span
                          data-testid={`unread-badge-${c.id}`}
                          className="bg-[#FF007F] text-black text-[10px] font-bold px-2 py-0.5 flex-shrink-0"
                        >
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-[#00FFFF] transition-colors" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
